import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/integrations/supabase/client';
import { UserPlus, Mail, Shield, User } from 'lucide-react';

interface InviteUserData {
  email: string;
  name: string;
  role: 'user' | 'staff' | 'admin';
}

const AdminInviteUser: React.FC = () => {
  const [formData, setFormData] = useState<InviteUserData>({
    email: '',
    name: '',
    role: 'user'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log('Invoking invite-user edge function with:', {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        sendWelcomeEmail: true
      });
      
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: formData.email,
          name: formData.name,
          role: formData.role,
          sendWelcomeEmail: true
        }
      });

      if (error) {
        console.error('Edge function error details:', {
          error,
          message: error?.message,
          details: error?.details,
          status: error?.status,
          stack: error?.stack
        });
        
        let errorMessage = 'Failed to invite user';
        if (typeof error === 'object' && error !== null) {
          if ('message' in error && error.message) {
            errorMessage = error.message as string;
          } else if ('details' in error && error.details) {
            errorMessage = error.details as string;
          }
        }
        
        toast.error(errorMessage);
        return;
      }

      if (data?.success) {
        console.log('User invited successfully:', data);
        setFormData({
          email: '',
          name: '',
          role: 'user'
        });
        toast.success(`User ${formData.name} invited successfully!`);
      } else {
        toast.error(data?.error || 'Failed to invite user');
      }
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error('Failed to invite user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof InviteUserData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <Shield className="h-4 w-4" />;
      case 'staff': return <UserPlus className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'admin': return 'Full system access, can manage all users and settings';
      case 'staff': return 'Can manage students and assign courses';
      case 'user': return 'Standard student account with course access';
      default: return '';
    }
  };

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Invite New User
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter full name"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter email address"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">User Role *</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => handleInputChange('role', value as InviteUserData['role'])}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select user role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">
                  <div className="flex items-center gap-2">
                    {getRoleIcon('user')}
                    <span>Student</span>
                  </div>
                </SelectItem>
                <SelectItem value="staff">
                  <div className="flex items-center gap-2">
                    {getRoleIcon('staff')}
                    <span>Staff</span>
                  </div>
                </SelectItem>
                <SelectItem value="admin">
                  <div className="flex items-center gap-2">
                    {getRoleIcon('admin')}
                    <span>Administrator</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {getRoleDescription(formData.role)}
            </p>
          </div>

          <div className="bg-blue-50 p-4 rounded-md">
            <h4 className="text-sm font-medium text-blue-800 mb-2">What happens next?</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• The user account will be created with the specified role</li>
              <li>• A secure temporary password will be generated</li>
              <li>• The user will receive account details via email</li>
              <li>• They can sign in and must change their password on first login</li>
            </ul>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setFormData({
                email: '',
                name: '',
                role: 'user'
              })}
              disabled={isSubmitting}
            >
              Clear
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending Invitation...' : 'Invite User'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AdminInviteUser;